import React, { useEffect, useState } from "react";
import { Form, Input, Button, Checkbox, notification, Spin, Tabs } from "antd";
import { Row, Col, Carousel } from "react-bootstrap";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
  MobileOutlined,
  KeyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { connect } from "react-redux";

import main from "../assets/main.png";
import logo from "../assets/logo.png";
import {
  loginAction,
  sendOtpAction,
  verifyOtpAction,
  setUser,
  clearAuthErrors,
} from "../store/action/common/authAction";

const RegistrationPage = (props) => {
  const [passwordForm] = Form.useForm();
  const [otpForm] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("password");
  const [identifierValue, setIdentifierValue] = useState("");
  const [isSwitchingTab, setIsSwitchingTab] = useState(false);

  // Notification Helper
  const showNotification = (type, message, description) => {
    notification[type]({
      message: message || "Notification",
      description: description || "",
      placement: "topRight",
    });
  };

  // Helper to check if input looks like a phone number
  const isPhoneLike = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return false;
    return /^[+\d][\d\s-]*$/.test(trimmed);
  };

  // Dynamic icon selector based on input format
  const getIdentifierIcon = () => {
    if (!identifierValue)
      return <UserOutlined className="text-muted mr-2" style={{ fontSize: "16px" }} />;
    if (isPhoneLike(identifierValue)) {
      return <MobileOutlined className="text-muted mr-2" style={{ fontSize: "16px" }} />;
    }
    return <MailOutlined className="text-muted mr-2" style={{ fontSize: "16px" }} />;
  };

  // Password Login Handler
  const handlePasswordLogin = (values) => {
    props.loginAction_({
      email: (values?.email || "").toLowerCase().trim(),
      password: values?.password,
    });
  };

  // Send OTP Handler
  const handleSendOtp = async () => {
    try {
      const values = await otpForm.validateFields(["identifier"]);
      const rawIdentifier = values?.identifier || identifierValue;
      if (!rawIdentifier) return;

      props.sendOtpAction_({
        identifier: String(rawIdentifier).trim(),
      });
    } catch (err) {
      // Form validation errors handled by Antd
    }
  };

  // Resend OTP Handler
  const handleResendOtp = () => {
    otpForm.setFieldsValue({ otp: "" });
    handleSendOtp();
  };

  // Verify OTP Handler
  const handleVerifyOtp = async (values) => {
    if (!props.otpSent) {
      handleSendOtp();
      return;
    }

    const rawIdentifier =
      values?.identifier || otpForm.getFieldValue("identifier") || identifierValue;
    const rawOtp = values?.otp || otpForm.getFieldValue("otp");

    if (!rawIdentifier || !rawOtp) {
      return;
    }

    props.verifyOtpAction_({
      identifier: String(rawIdentifier).trim(),
      otp: String(rawOtp).trim(),
    });
  };

  // Login Success Handler
  const handleLoginSuccess = (loginData) => {
    const authData = loginData?.data?.accessToken
      ? loginData.data
      : loginData?.data || loginData;
    const userData = authData?.userData;

    if (!authData || !authData.accessToken || !userData) {
      return;
    }

    const resolvedUserId = userData.userId ?? userData.initialUserId ?? userData._id ?? "";
    const resolvedMongoId = userData._id || "";

    localStorage.setItem("accessToken", authData.accessToken);
    localStorage.setItem("refreshToken", authData.refreshToken || "");
    localStorage.setItem("email", JSON.stringify(userData.email || ""));
    localStorage.setItem("role", userData.role || "");
    localStorage.setItem("name", userData.name || "");
    localStorage.setItem("userId", String(resolvedUserId));
    localStorage.setItem("id", String(resolvedMongoId));
    localStorage.setItem("phone", userData.mobileNo || "");
    localStorage.setItem("companyName", userData.companyName || "");
    localStorage.setItem("firstLogin", String(userData.isFirstLogin ?? false));
    localStorage.setItem("country", userData.country || "");
    localStorage.setItem("domain", userData.categoryName || "");
    localStorage.setItem("domainid", String(userData.category || ""));
    localStorage.setItem(
      "subscriptionSignup",
      userData.subscriptionSignup ? "true" : "false"
    );

    props.setUser_({
      email: userData.email,
      role: userData.role,
      name: userData.name,
    });

    if (userData?.role === "business") {
      navigate("/");
    } else if (userData?.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  // Prefill Signup Email
  useEffect(() => {
    if (location.state?.signupEmail) {
      const initialEmail = location.state.signupEmail.toLowerCase();
      passwordForm.setFieldsValue({ email: initialEmail });
      otpForm.setFieldsValue({ identifier: initialEmail });
      setIdentifierValue(initialEmail);
    }
  }, [location.state, passwordForm, otpForm]);

  // Handle OTP Dispatch API Responses (Success/Failure Payload)
  useEffect(() => {
    if (isSwitchingTab || activeTab !== "otp") return;

    const res = props.otpResponse;
    const currentIdentifier = otpForm.getFieldValue("identifier");

    if (!res || !currentIdentifier) return;

    const msg = res?.message || "Operation completed";

    if (res?.success === true) {
      showNotification("success", "OTP Sent", msg);
    } else if (res?.success === false) {
      showNotification("error", "OTP Request Failed", msg);
      otpForm.setFields([
        {
          name: "identifier",
          errors: [msg],
        },
      ]);
    }
  }, [props.otpResponse, activeTab, isSwitchingTab, otpForm]);

  // Handle Main Login Response (Password or OTP Auth Success/Failure)
  useEffect(() => {
    if (isSwitchingTab || !props.loginResponse) return;

    const res = props.loginResponse;
    const msg = res?.message || "Request completed";
    const activePayload = res?.data?.accessToken ? res.data : res?.data || res;

    if (activePayload?.accessToken) {
      handleLoginSuccess(res);
      return;
    }

    if (res?.success === false) {
      if (activeTab === "password" && passwordForm.getFieldValue("email")) {
        showNotification("error", "Login Failed", msg);
        passwordForm.setFields([
          {
            name: "password",
            errors: [msg],
          },
        ]);
      } else if (activeTab === "otp" && props.otpSent && otpForm.getFieldValue("otp")) {
        showNotification("error", "OTP Verification Failed", msg);
        otpForm.setFields([
          {
            name: "otp",
            errors: [msg],
          },
        ]);
      }
    }
  }, [props.loginResponse, activeTab, props.otpSent, isSwitchingTab, passwordForm, otpForm]);

  // Handle Password Redux Failures (FETCH_LOGIN_FAILURE)
  useEffect(() => {
    if (isSwitchingTab || activeTab !== "password") return;

    const emailVal = passwordForm.getFieldValue("email");
    if (props.error && emailVal) {
      const errMsg =
        props.error?.message ||
        props.error?.response?.data?.message ||
        (typeof props.error === "string" ? props.error : "Invalid credentials");

      showNotification("error", "Login Failed", errMsg);

      passwordForm.setFields([
        {
          name: "password",
          errors: [errMsg],
        },
      ]);
    }
  }, [props.error, activeTab, isSwitchingTab, passwordForm]);

  // Handle OTP Verification Redux Failures (VERIFY_OTP_FAILURE)
  useEffect(() => {
    if (isSwitchingTab || activeTab !== "otp") return;

    const currentError = props.otpError || props.error;
    const identifierVal = otpForm.getFieldValue("identifier");

    if (currentError && identifierVal) {
      const errMsg =
        currentError?.message ||
        currentError?.response?.data?.message ||
        (typeof currentError === "string" ? currentError : "Invalid OTP provided");

      showNotification("error", "OTP Verification Failed", errMsg);

      const targetField = props.otpSent ? "otp" : "identifier";
      otpForm.setFields([
        {
          name: targetField,
          errors: [errMsg],
        },
      ]);
    }
  }, [props.otpError, props.error, activeTab, props.otpSent, isSwitchingTab, otpForm]);

  // Tab Switch Handler
  const handleTabChange = (key) => {
    setIsSwitchingTab(true);
    setActiveTab(key);
    setIdentifierValue("");

    passwordForm.resetFields();
    otpForm.resetFields();

    passwordForm.setFields([
      { name: "email", value: "", errors: [] },
      { name: "password", value: "", errors: [] },
    ]);
    otpForm.setFields([
      { name: "identifier", value: "", errors: [] },
      { name: "otp", value: "", errors: [] },
    ]);

    if (props.clearAuthErrors_) {
      props.clearAuthErrors_();
    }

    setTimeout(() => {
      setIsSwitchingTab(false);
    }, 100);
  };

  const inputStyle = {
    borderRadius: "6px",
    height: "42px",
    fontSize: "15px",
  };

  const primaryButtonStyle = {
    width: "100%",
    borderRadius: "6px",
    height: "44px",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: "#0048FF",
    borderColor: "#0048FF",
  };

  const labelStyle = {
    fontSize: "15px",
    fontWeight: "500",
    color: "#333",
  };

  const tabItems = [
    {
      key: "password",
      label: <span style={{ fontSize: "16px", fontWeight: "600" }}>Password Login</span>,
      children: (
        <Form
          form={passwordForm}
          name="passwordLoginForm"
          layout="vertical"
          onFinish={handlePasswordLogin}
          style={{ marginTop: "6px" }}
        >
          <Form.Item
            name="email"
            label={<span style={labelStyle}>Email Address</span>}
            style={{ marginBottom: "12px" }}
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Please enter a valid email address!" },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-muted mr-2" style={{ fontSize: "16px" }} />}
              placeholder="name@company.com"
              style={inputStyle}
              onChange={(e) => {
                const lower = e.target.value.toLowerCase();
                passwordForm.setFieldsValue({ email: lower });
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={<span style={labelStyle}>Password</span>}
            style={{ marginBottom: "12px" }}
            rules={[
              { required: true, message: "Please input your password!" },
              { min: 6, message: "Password must be at least 6 characters!" },
            ]}
          >
            <Input.Password
              prefix={<KeyOutlined className="text-muted mr-2" style={{ fontSize: "16px" }} />}
              placeholder="Enter password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              style={inputStyle}
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "14px",
              fontSize: "14px",
            }}
          >
            <Checkbox style={{ fontSize: "14px" }}>Remember me</Checkbox>
            <Link to="/forgot-password" style={{ color: "#0048FF", fontSize: "14px", fontWeight: "500" }}>
              Forgot / Reset password
            </Link>
          </div>

          <Form.Item style={{ marginBottom: "0" }}>
            <Button
              type="primary"
              htmlType="submit"
              style={primaryButtonStyle}
              loading={props.loading}
              disabled={props.loading}
            >
              {props.loading ? <Spin /> : "Sign In"}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "otp",
      label: <span style={{ fontSize: "15px", fontWeight: "600" }}>OTP Login</span>,
      children: (
        <Form
          form={otpForm}
          name="otpLoginForm"
          layout="vertical"
          onFinish={handleVerifyOtp}
          style={{ marginTop: "6px" }}
        >
          <Form.Item
            name="identifier"
            label={<span style={labelStyle}>Email or Phone Number</span>}
            style={{ marginBottom: "12px" }}
            rules={[
              {
                required: true,
                message: "Please enter your email or phone number!",
              },
            ]}
          >
            <Input
              prefix={getIdentifierIcon()}
              placeholder="Enter Email or Mobile Number"
              style={inputStyle}
              onPressEnter={(e) => {
                if (!props.otpSent) {
                  e.preventDefault();
                  handleSendOtp();
                }
              }}
              onChange={(e) => setIdentifierValue(e.target.value)}
            />
          </Form.Item>

          {props.otpSent && (
            <Form.Item
              name="otp"
              label={<span style={labelStyle}>Verification Code</span>}
              style={{ marginBottom: "12px" }}
              rules={[
                { required: true, message: "Please enter the OTP!" },
                { len: 6, message: "OTP must be exactly 6 digits!" },
              ]}
            >
              <Input
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                style={{ ...inputStyle, letterSpacing: "2px" }}
                onChange={() => {
                  if (otpForm.getFieldError("otp").length > 0) {
                    otpForm.setFields([{ name: "otp", errors: [] }]);
                  }
                }}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: "0", marginTop: "12px" }}>
            {!props.otpSent ? (
              <Button
                type="primary"
                onClick={handleSendOtp}
                style={primaryButtonStyle}
                loading={props.otpLoading}
                disabled={props.otpLoading}
              >
                Send OTP
              </Button>
            ) : (
              <div className="d-flex flex-column gap-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  style={primaryButtonStyle}
                  loading={props.loading}
                  disabled={props.loading}
                >
                  Verify & Sign In
                </Button>
                <Button
                  type="link"
                  onClick={handleResendOtp}
                  loading={props.otpLoading}
                  disabled={props.otpLoading}
                  style={{ color: "#0048FF", textAlign: "center", fontSize: "14px", fontWeight: "500", padding: 0 }}
                >
                  Resend OTP
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div
      className="container-fluid p-0 overflow-hidden"
      style={{ height: "100vh", backgroundColor: "#E6EDF3" }}
    >
      <Row className="g-0 h-100">
        <Col
          md={6}
          className="d-flex flex-column justify-content-center align-items-center bg-white p-3 p-md-4 h-100"
          style={{ overflowY: "auto" }}
        >
          <div style={{ maxWidth: "420px", width: "100%" }}>
            <img
              src={logo}
              alt="Logo"
              className="mb-2"
              style={{ width: "120px" }}
            />
            <h1 className="mb-1" style={{ fontSize: "1.8rem", fontWeight: "500" }}>
              Welcome Back
            </h1>
            <p className="text-muted mb-3" style={{ fontSize: "1rem" }}>
              Please enter your details to log in
            </p>

            {location.state?.showPasswordHint && (
              <p style={{ color: "green", fontWeight: "600", fontSize: "0.95rem", marginBottom: "10px" }}>
                Password has been sent to your email
              </p>
            )}

            <div
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                border: "1px solid #F0F0F0",
              }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                centered
              />
            </div>

            <p className="mt-3 text-center mb-0" style={{ fontSize: "1rem" }}>
              Don't have an account?{" "}
              <a
                href="https://stageretailindia.blaccdot.ca/sign-up"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-weight-bold"
                style={{ fontWeight: "600" }}
              >
                Sign up
              </a>
            </p>
          </div>
        </Col>

        <Col
          md={6}
          className="d-none d-md-flex flex-column justify-content-center align-items-center text-white h-100 loginCarousel"
          style={{ backgroundColor: "#075AD8", textAlign: "center" }}
        >
          <Carousel
            controls={false}
            indicators={false}
            interval={3000}
            pause={false}
            style={{ width: "100%" }}
          >
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-1"
                className="d-block"
                style={{ width: "55%", margin: "auto" }}
              />
            </Carousel.Item>
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-2"
                className="d-block"
                style={{ width: "55%", margin: "auto" }}
              />
            </Carousel.Item>
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-3"
                className="d-block"
                style={{ width: "55%", margin: "auto" }}
              />
            </Carousel.Item>
          </Carousel>

          <h2 style={{ fontSize: "1.5rem", marginTop: "16px", fontWeight: "600" }}>
            Start Converting Leads Fast
          </h2>
          <p
            className="text-center"
            style={{ maxWidth: "380px", fontSize: "1rem", marginTop: "8px", marginBottom: 0 }}
          >
            Unlock exclusive insights and opportunities—log in to grow your business!
          </p>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loginResponse: state.login.response,
  loading: state.login.loading,
  error: state.login.error,
  otpSent: state.login.otpSent,
  otpLoading: state.login.otpLoading,
  otpError: state.login.otpError,
  otpResponse: state.login.otpResponse,
});

const mapDispatchToProps = (dispatch) => ({
  loginAction_: (data) => {
    dispatch(loginAction(data));
  },
  sendOtpAction_: (data) => {
    dispatch(sendOtpAction(data));
  },
  verifyOtpAction_: (data) => {
    dispatch(verifyOtpAction(data));
  },
  setUser_: (data) => {
    dispatch(setUser(data));
  },
  clearAuthErrors_: () => {
    dispatch(clearAuthErrors());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RegistrationPage);

// import React, { useEffect, useState } from "react";
// import { Form, Input, Button, Checkbox, notification, Spin, Tabs } from "antd";
// import { Row, Col, Carousel } from "react-bootstrap";
// import {
//   EyeInvisibleOutlined,
//   EyeTwoTone,
//   MailOutlined,
//   MobileOutlined,
//   KeyOutlined,
//   UserOutlined,
// } from "@ant-design/icons";
// import { useNavigate, useLocation, Link } from "react-router-dom";
// import { connect } from "react-redux";

// import main from "../assets/main.png";
// import logo from "../assets/logo.png";
// import {
//   loginAction,
//   sendOtpAction,
//   verifyOtpAction,
//   setUser,
// } from "../store/action/common/authAction";

// const RegistrationPage = (props) => {
//   const [passwordForm] = Form.useForm();
//   const [otpForm] = Form.useForm();

//   const navigate = useNavigate();
//   const location = useLocation();

//   const [activeTab, setActiveTab] = useState("password");
//   const [identifierValue, setIdentifierValue] = useState("");

//   // Notification Helper
//   const showNotification = (type, message, description) => {
//     notification[type]({
//       message: message || "Notification",
//       description: description || "",
//       placement: "topRight",
//     });
//   };

//   // Helper to check if input looks like a phone number
//   const isPhoneLike = (value) => {
//     const trimmed = (value || "").trim();
//     if (!trimmed) return false;
//     return /^[+\d][\d\s-]*$/.test(trimmed);
//   };

//   // Dynamic icon selector based on input format
//   const getIdentifierIcon = () => {
//     if (!identifierValue) return <UserOutlined className="text-muted mr-2" />;
//     if (isPhoneLike(identifierValue)) {
//       return <MobileOutlined className="text-muted mr-2" />;
//     }
//     return <MailOutlined className="text-muted mr-2" />;
//   };

//   // Password Login Handler
//   const handlePasswordLogin = (values) => {
//     props.loginAction_({
//       email: (values?.email || "").toLowerCase().trim(),
//       password: values?.password,
//     });
//   };

//   // Send OTP Handler
//   const handleSendOtp = async () => {
//     try {
//       const values = await otpForm.validateFields(["identifier"]);
//       const rawIdentifier = values?.identifier || identifierValue;
//       if (!rawIdentifier) return;

//       props.sendOtpAction_({
//         identifier: String(rawIdentifier).trim(),
//       });
//     } catch (err) {
//       // Form validation errors handled by Antd
//     }
//   };

//   // Resend OTP Handler
//   const handleResendOtp = () => {
//     otpForm.setFieldsValue({ otp: "" });
//     handleSendOtp();
//   };

//   // Verify OTP Handler (Guarded against undefined values)
//   const handleVerifyOtp = async (values) => {
//     // If OTP input is not yet mounted/sent, route "Enter" key press to Send OTP
//     if (!props.otpSent) {
//       handleSendOtp();
//       return;
//     }

//     const rawIdentifier = values?.identifier || otpForm.getFieldValue("identifier") || identifierValue;
//     const rawOtp = values?.otp || otpForm.getFieldValue("otp");

//     if (!rawIdentifier || !rawOtp) {
//       return;
//     }

//     props.verifyOtpAction_({
//       identifier: String(rawIdentifier).trim(),
//       otp: String(rawOtp).trim(),
//     });
//   };

//   // Login Success Handler
//   const handleLoginSuccess = (loginData) => {
//     const authData = loginData?.data?.accessToken
//       ? loginData.data
//       : loginData?.data || loginData;
//     const userData = authData?.userData;

//     if (!authData || !authData.accessToken || !userData) {
//       return;
//     }

//     // showNotification(
//     //   "success",
//     //   "Login Successful",
//     //   loginData?.message || "Welcome back!"
//     // );

//     const resolvedUserId =
//       userData.userId ?? userData.initialUserId ?? userData._id ?? "";
//     const resolvedMongoId = userData._id || "";

//     localStorage.setItem("accessToken", authData.accessToken);
//     localStorage.setItem("refreshToken", authData.refreshToken || "");
//     localStorage.setItem("email", JSON.stringify(userData.email || ""));
//     localStorage.setItem("role", userData.role || "");
//     localStorage.setItem("name", userData.name || "");
//     localStorage.setItem("userId", String(resolvedUserId));
//     localStorage.setItem("id", String(resolvedMongoId));
//     localStorage.setItem("phone", userData.mobileNo || "");
//     localStorage.setItem("companyName", userData.companyName || "");
//     localStorage.setItem("firstLogin", String(userData.isFirstLogin ?? false));
//     localStorage.setItem("country", userData.country || "");
//     localStorage.setItem("domain", userData.categoryName || "");
//     localStorage.setItem("domainid", String(userData.category || ""));
//     localStorage.setItem(
//       "subscriptionSignup",
//       userData.subscriptionSignup ? "true" : "false"
//     );

//     props.setUser_({
//       email: userData.email,
//       role: userData.role,
//       name: userData.name,
//     });

//     if (userData?.role === "business") {
//       navigate("/");
//     } else if (userData?.role === "admin") {
//       navigate("/admin");
//     } else {
//       navigate("/");
//     }
//   };

//   // Prefill Signup Email
//   useEffect(() => {
//     if (location.state?.signupEmail) {
//       const initialEmail = location.state.signupEmail.toLowerCase();
//       passwordForm.setFieldsValue({ email: initialEmail });
//       otpForm.setFieldsValue({ identifier: initialEmail });
//       setIdentifierValue(initialEmail);
//     }
//   }, [location.state, passwordForm, otpForm]);

//   // -------------------------------------------------------------
//   // RESPONSE LISTENERS (HANDLES "OTP SENT SUCCESSFULLY" & OTHERS)
//   // -------------------------------------------------------------

//   // Listener for Send OTP Response (Inspects both otpResponse & loginResponse fallback)
//   // useEffect(() => {
//   //   const res = props.otpResponse || (props.otpSent ? props.loginResponse : null);
//   //   if (!res) return;

//   //   const msg = res?.message || "Operation completed";

//   //   if (res?.success === true) {
//   //     showNotification("success", "OTP Sent", msg);
//   //   } else if (res?.success === false) {
//   //     showNotification("error", "OTP Request Failed", msg);
//   //     otpForm.setFields([
//   //       {
//   //         name: "identifier",
//   //         errors: [msg],
//   //       },
//   //     ]);
//   //   }
//   // }, [props.otpResponse, props.loginResponse, props.otpSent, otpForm]);


//   useEffect(() => {
//   const res = props.otpResponse;
//   if (!res) return;

//   const msg = res?.message || "Operation completed";

//   if (res?.success === true) {
//     showNotification("success", "OTP Sent", msg);
//   } else if (res?.success === false) {
//     showNotification("error", "OTP Request Failed", msg);
//     otpForm.setFields([
//       {
//         name: "identifier",
//         errors: [msg],
//       },
//     ]);
//   }
// }, [props.otpResponse, otpForm]);
//   // Listener for Password Login & OTP Verification Responses
//   useEffect(() => {
//     if (!props.loginResponse) return;

//     const res = props.loginResponse;
//     const msg = res?.message || "Request completed";
//     const activePayload = res?.data?.accessToken ? res.data : res?.data || res;

//     if (activePayload?.accessToken) {
//       handleLoginSuccess(res);
//       return;
//     }

//     if (res?.success === false) {
//       if (activeTab === "password") {
//         showNotification("error", "Login Failed", msg);
//         passwordForm.setFields([
//           {
//             name: "password",
//             errors: [msg],
//           },
//         ]);
//       } else if (activeTab === "otp" && props.otpSent) {
//         showNotification("error", "OTP Verification Failed", msg);
//         otpForm.setFields([
//           {
//             name: "otp",
//             errors: [msg],
//           },
//         ]);
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [props.loginResponse, activeTab, props.otpSent, passwordForm, otpForm]);

//   // Password Error Catch-All (HTTP 4xx/5xx)
//   useEffect(() => {
//     if (props.error && activeTab === "password") {
//       const errMsg =
//         props.error?.response?.data?.message ||
//         props.error?.message ||
//         (typeof props.error === "string" ? props.error : "Invalid credentials");

//       showNotification("error", "Login Failed", errMsg);

//       passwordForm.setFields([
//         {
//           name: "password",
//           errors: [errMsg],
//         },
//       ]);
//     }
//   }, [props.error, activeTab, passwordForm]);

//   // OTP Error Catch-All (HTTP 4xx/5xx)
//   useEffect(() => {
//     if (props.otpError && activeTab === "otp") {
//       const errMsg =
//         props.otpError?.response?.data?.message ||
//         props.otpError?.message ||
//         (typeof props.otpError === "string" ? props.otpError : "Invalid OTP provided");

//       showNotification("error", "OTP Error", errMsg);

//       otpForm.setFields([
//         {
//           name: props.otpSent ? "otp" : "identifier",
//           errors: [errMsg],
//         },
//       ]);
//     }
//   }, [props.otpError, activeTab, props.otpSent, otpForm]);

//   // Tab Items Configuration
//   const tabItems = [
//     {
//       key: "password",
//       label: "Password Login",
//       children: (
//         <Form
//           form={passwordForm}
//           name="passwordLoginForm"
//           layout="vertical"
//           onFinish={handlePasswordLogin}
//         >
//           <Form.Item
//             name="email"
//             label="Email Address"
//             rules={[
//               { required: true, message: "Please input your email!" },
//               { type: "email", message: "Please enter a valid email address!" },
//             ]}
//           >
//             <Input
//               prefix={<MailOutlined className="text-muted mr-2" />}
//               placeholder="name@company.com"
//               size="large"
//               style={{ borderRadius: "5px" }}
//               onChange={(e) => {
//                 const lower = e.target.value.toLowerCase();
//                 passwordForm.setFieldsValue({ email: lower });
//               }}
//             />
//           </Form.Item>

//           <Form.Item
//             name="password"
//             label="Password"
//             rules={[
//               { required: true, message: "Please input your password!" },
//               { min: 6, message: "Password must be at least 6 characters!" },
//             ]}
//           >
//             <Input.Password
//               prefix={<KeyOutlined className="text-muted mr-2" />}
//               placeholder="Enter password"
//               size="large"
//               iconRender={(visible) =>
//                 visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
//               }
//               style={{ borderRadius: "5px" }}
//             />
//           </Form.Item>

//           <div
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               marginBottom: "16px",
//             }}
//           >
//             <Checkbox>Remember me</Checkbox>
//             <Link to="/forgot-password" style={{ color: "#0048FF" }}>
//               Forgot / Reset password
//             </Link>
//           </div>

//           <Form.Item>
//             <Button
//               type="primary"
//               htmlType="submit"
//               size="large"
//               style={{
//                 width: "100%",
//                 borderRadius: "5px",
//                 backgroundColor: "#0048FF",
//                 borderColor: "#0048FF",
//               }}
//               loading={props.loading}
//               disabled={props.loading}
//             >
//               {props.loading ? <Spin /> : "Sign In"}
//             </Button>
//           </Form.Item>
//         </Form>
//       ),
//     },
//     {
//       key: "otp",
//       label: "OTP Login",
//       children: (
//         <Form
//           form={otpForm}
//           name="otpLoginForm"
//           layout="vertical"
//           onFinish={handleVerifyOtp}
//         >
//           <Form.Item
//             name="identifier"
//             label="Email or Phone Number"
//             rules={[
//               {
//                 required: true,
//                 message: "Please enter your email or phone number!",
//               },
//             ]}
//           >
//             <Input
//               prefix={getIdentifierIcon()}
//               placeholder="Enter Email or Mobile Number"
//               size="large"
//               style={{ borderRadius: "5px" }}
//               disabled={false}
//               onPressEnter={(e) => {
//                 if (!props.otpSent) {
//                   e.preventDefault();
//                   handleSendOtp();
//                 }
//               }}
//               onChange={(e) => setIdentifierValue(e.target.value)}
//             />
//           </Form.Item>

//           {props.otpSent && (
//             <Form.Item
//               name="otp"
//               label="Verification Code"
//               rules={[
//                 { required: true, message: "Please enter the OTP!" },
//                 { len: 6, message: "OTP must be exactly 6 digits!" },
//               ]}
//             >
//               <Input
//                 placeholder="Enter 6-digit OTP"
//                 size="large"
//                 maxLength={6}
//                 style={{ borderRadius: "5px", letterSpacing: "2px" }}
//                 onChange={() => {
//                   if (otpForm.getFieldError("otp").length > 0) {
//                     otpForm.setFields([{ name: "otp", errors: [] }]);
//                   }
//                 }}
//               />
//             </Form.Item>
//           )}

//           <Form.Item style={{ marginTop: "24px" }}>
//             {!props.otpSent ? (
//               <Button
//                 type="primary"
//                 size="large"
//                 onClick={handleSendOtp}
//                 style={{
//                   width: "100%",
//                   borderRadius: "5px",
//                   backgroundColor: "#0048FF",
//                   borderColor: "#0048FF",
//                 }}
//                 loading={props.otpLoading}
//                 disabled={props.otpLoading}
//               >
//                 Send OTP
//               </Button>
//             ) : (
//               <div className="d-flex flex-column gap-2">
//                 <Button
//                   type="primary"
//                   htmlType="submit"
//                   size="large"
//                   style={{
//                     width: "100%",
//                     borderRadius: "5px",
//                     backgroundColor: "#0048FF",
//                     borderColor: "#0048FF",
//                   }}
//                   loading={props.loading}
//                   disabled={props.loading}
//                 >
//                   Verify & Sign In
//                 </Button>
//                 <Button
//                   type="link"
//                   onClick={handleResendOtp}
//                   loading={props.otpLoading}
//                   disabled={props.otpLoading}
//                   style={{ color: "#0048FF", textAlign: "center" }}
//                 >
//                   Resend OTP
//                 </Button>
//               </div>
//             )}
//           </Form.Item>
//         </Form>
//       ),
//     },
//   ];

//   // Reset form fields and clear state when switching tabs
// const handleTabChange = (key) => {
//   setActiveTab(key);
//   setIdentifierValue("");

//   // Clear password form
//   passwordForm.resetFields();

//   // Clear OTP form
//   otpForm.resetFields();

//   // Reset Redux / Local error states if applicable via dispatch, 
//   // or clear field-level errors explicitly:
//   passwordForm.setFields([
//     { name: "email", errors: [] },
//     { name: "password", errors: [] },
//   ]);
//   otpForm.setFields([
//     { name: "identifier", errors: [] },
//     { name: "otp", errors: [] },
//   ]);
// };

//   return (
//     <div
//       className="container-fluid"
//       style={{ height: "100vh", backgroundColor: "#E6EDF3" }}
//     >
//       <Row className="h-100">
//         <Col
//           md={6}
//           className="d-flex flex-column justify-content-center align-items-center bg-white p-5"
//         >
//           <div style={{ maxWidth: "400px", width: "100%" }}>
//             <img
//               src={logo}
//               alt="Logo"
//               className="mb-4"
//               style={{ width: "120px" }}
//             />
//             <h1 className="mb-1" style={{ fontSize: "1.8rem" }}>
//               Welcome Back
//             </h1>
//             <p className="text-muted mb-4">
//               Please enter your details to log in
//             </p>

//             {location.state?.showPasswordHint && (
//               <p style={{ color: "green", fontWeight: "500" }}>
//                 Password has been sent to your email
//               </p>
//             )}

//             <div
//               style={{
//                 background: "#fff",
//                 padding: "20px",
//                 borderRadius: "8px",
//                 boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               <Tabs
//                 activeKey={activeTab}
//                 // onChange={(key) => setActiveTab(key)}
//                 onChange={handleTabChange}
//                 items={tabItems}
//                 centered
//               />
//             </div>

//             <p className="mt-4 text-center">
//               Don't have an account?{" "}
//               <a
//                 href="https://stageretailindia.blaccdot.ca/sign-up"
//                 target="_blank"
//                 rel="noreferrer"
//                 className="text-primary font-weight-bold"
//               >
//                 Sign up
//               </a>
//             </p>
//           </div>
//         </Col>

//         <Col
//           md={6}
//           className="d-none d-md-flex flex-column justify-content-center align-items-center text-white loginCarousel"
//           style={{ backgroundColor: "#075AD8", textAlign: "center" }}
//         >
//           <Carousel
//             controls={false}
//             indicators={false}
//             interval={3000}
//             pause={false}
//           >
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-1"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-2"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-3"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//           </Carousel>

//           <h2 style={{ fontSize: "1.5rem", marginTop: "20px" }}>
//             Start Converting Leads Fast
//           </h2>
//           <p
//             className="text-center"
//             style={{ maxWidth: "400px", fontSize: "1rem", marginTop: "10px" }}
//           >
//             Unlock exclusive insights and opportunities—log in to grow your
//             business!
//           </p>
//         </Col>
//       </Row>
//     </div>
//   );
// };

// const mapStateToProps = (state) => ({
//   loginResponse: state.login.response,
//   loading: state.login.loading,
//   error: state.login.error,
//   otpSent: state.login.otpSent,
//   otpLoading: state.login.otpLoading,
//   otpError: state.login.otpError,
//   otpResponse: state.login.otpResponse,
// });

// const mapDispatchToProps = (dispatch) => ({
//   loginAction_: (data) => {
//     dispatch(loginAction(data));
//   },
//   sendOtpAction_: (data) => {
//     dispatch(sendOtpAction(data));
//   },
//   verifyOtpAction_: (data) => {
//     dispatch(verifyOtpAction(data));
//   },
//   setUser_: (data) => {
//     dispatch(setUser(data));
//   },
// });

// export default connect(mapStateToProps, mapDispatchToProps)(RegistrationPage);
